import React, { useState } from 'react';
import { Mail, Instagram, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    
    setFormData({
      name: '',
      email: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            Get In <span className="text-orange-500">Touch</span>
          </h1>
          <p className="text-xl text-gray-400">
            Questions about our sauces? Want to know about the next batch? Reach out.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2 className="text-3xl font-black mb-6">Send Us a Message</h2>
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold mb-2 text-gray-300">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-12"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold mb-2 text-gray-300">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-12"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-bold mb-2 text-gray-300">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us what's on your mind..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold text-lg py-6"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-black mb-6">Other Ways to Connect</h2>
            
            <div className="space-y-6">
              {/* Email */}
              <Card className="bg-gray-900 border-gray-800 hover:bg-gray-800/70 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">Email</h3>
                      <p className="text-gray-400 mb-2">
                        For order inquiries and general questions
                      </p>
                      <a 
                        href="mailto:admin@hhhotsauce.org"
                        className="text-orange-400 hover:text-orange-300 font-medium transition-colors"
                      >
                        admin@hhhotsauce.org
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Instagram */}
              <Card className="bg-gray-900 border-gray-800 hover:bg-gray-800/70 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">Instagram</h3>
                      <p className="text-gray-400 mb-2">
                        Follow our journey and see behind the scenes
                      </p>
                      <a 
                        href="https://instagram.com/chemistry.dj"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-400 hover:text-orange-300 font-medium transition-colors"
                      >
                        @chemistry.dj
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Response Time */}
              <Card className="bg-gradient-to-br from-orange-900/20 to-red-900/20 border-orange-600/50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-orange-400">Response Time</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We typically respond to all inquiries within 24 hours. If you're asking about the next batch drop or order status, we'll get back to you as soon as possible.
                  </p>
                </CardContent>
              </Card>

              {/* FAQ */}
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4 text-white">Quick Questions</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-orange-400 font-medium">When is the next batch?</p>
                      <p className="text-gray-400 text-sm">Sign up for email notifications on the homepage!</p>
                    </div>
                    <div>
                      <p className="text-orange-400 font-medium">Do you ship?</p>
                      <p className="text-gray-400 text-sm">Local delivery and pickup available. Contact us for details.</p>
                    </div>
                    <div>
                      <p className="text-orange-400 font-medium">Can I request a custom batch?</p>
                      <p className="text-gray-400 text-sm">Email us to discuss custom orders for larger quantities.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
